function normalizeApiBase(url: string) {
  return url.replace(/\/+$/, "");
}

export const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL
  ? normalizeApiBase(process.env.NEXT_PUBLIC_API_BASE_URL)
  : typeof window !== "undefined"
    ? `${window.location.protocol}//${window.location.hostname}:4000/api`
    : "http://127.0.0.1:4000/api";

export function resolveApiBase() {
  return API_BASE;
}

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type FetchOptions = RequestInit & {
  next?: { revalidate?: number; tags?: string[] };
  cookies?: string;
};

function parseCookieString(raw?: string) {
  if (!raw) return {};
  return raw.split(";").reduce<Record<string, string>>((acc, part) => {
    const [k, ...rest] = part.trim().split("=");
    if (!k) return acc;
    acc[decodeURIComponent(k)] = decodeURIComponent(rest.join("="));
    return acc;
  }, {});
}

export function readCsrfToken(cookiesHeader?: string) {
  if (typeof document !== "undefined") {
    return document.cookie
      .split(";")
      .map((c) => c.trim())
      .map((part) => part.split("="))
      .find(([key]) => key === "eg_csrf")?.[1];
  }
  if (cookiesHeader) {
    const cookies = parseCookieString(cookiesHeader);
    return cookies["eg_csrf"];
  }
  return undefined;
}

export async function fetchJSON<T>(
  path: string,
  init?: FetchOptions,
): Promise<T> {
  const { cache = "no-store", cookies, headers, ...rest } = init ?? {};
  const mergedHeaders = new Headers(headers ?? {});
  const method = (rest.method ?? "GET").toUpperCase();
  if (!mergedHeaders.has("Content-Type") && rest.body) {
    mergedHeaders.set("Content-Type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = readCsrfToken(
      cookies ||
        (typeof headers === "object" ? (headers as any).cookie : undefined),
    );
    if (csrf && !mergedHeaders.has("x-csrf-token")) {
      mergedHeaders.set("x-csrf-token", csrf);
    }
  }

  if (cookies && !mergedHeaders.has("cookie")) {
    mergedHeaders.set("cookie", cookies);
  }

  const res = await fetch(`${resolveApiBase()}${path}`, {
    cache,
    credentials: "include",
    ...rest,
    headers: mergedHeaders,
  });
  const contentType = res.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await res.json().catch(() => undefined)
    : undefined;

  if (!res.ok) {
    throw new ApiError(
      `Request failed ${path}: ${res.status}`,
      res.status,
      body,
    );
  }
  return body as T;
}
